import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';

interface DateRangePillProps {
  label: string;
  startValue: string;
  endValue: string;
  onStartChange: (val: string) => void;
  onEndChange: (val: string) => void;
  minWidth?: number;
}

const DateRangePill: React.FC<DateRangePillProps> = ({
  label,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  minWidth = 300,
}) => {
  const [startFocused, setStartFocused] = useState(false);
  const [endFocused, setEndFocused] = useState(false);
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth,
        width: { xs: '100%', xl: 'auto' },
      }}
    >
      <Typography
        sx={{
          position: 'absolute',
          top: -9,
          left: 10,
          fontSize: '11px',
          fontWeight: 700,
          color: '#213B54',
          backgroundColor: 'white',
          px: 0.5,
          lineHeight: 1,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #213B54',
          borderRadius: '10px',
          px: 1.5,
          height: 40,
          gap: 0.5,
          '& input[type="date"]::-webkit-calendar-picker-indicator': {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          {!startValue && !startFocused && (
            <Typography
              sx={{
                position: 'absolute',
                fontSize: '13px',
                color: '#BDBDBD',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Start
            </Typography>
          )}
          <input
            ref={startRef}
            type="date"
            value={startValue}
            onChange={(e) => onStartChange(e.target.value)}
            onFocus={() => setStartFocused(true)}
            onBlur={() => setStartFocused(false)}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '13px',
              color: startValue || startFocused ? '#213B54' : 'transparent',
              backgroundColor: 'transparent',
              width: '100%',
              cursor: 'pointer',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => (startRef.current as any)?.showPicker()}
          sx={{ p: 0.25, color: '#213B54', flexShrink: 0 }}
        >
          <CalendarTodayIcon sx={{ fontSize: 14 }} />
        </IconButton>

        <Typography sx={{ color: '#BDBDBD', mx: 0.5, flexShrink: 0 }}>
          →
        </Typography>

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
          }}
        >
          {!endValue && !endFocused && (
            <Typography
              sx={{
                position: 'absolute',
                fontSize: '13px',
                color: '#BDBDBD',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              End
            </Typography>
          )}
          <input
            ref={endRef}
            type="date"
            value={endValue}
            onChange={(e) => onEndChange(e.target.value)}
            onFocus={() => setEndFocused(true)}
            onBlur={() => setEndFocused(false)}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '13px',
              color: endValue || endFocused ? '#213B54' : 'transparent',
              backgroundColor: 'transparent',
              width: '100%',
              cursor: 'pointer',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          />
        </Box>
        <IconButton
          size="small"
          onClick={() => (endRef.current as any)?.showPicker()}
          sx={{ p: 0.25, color: '#213B54', flexShrink: 0 }}
        >
          <CalendarTodayIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DateRangePill;
